import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
    ArrowRight,
    CreditCard,
    Mail,
    Sparkles,
    Check,
    AlertTriangle,
    Zap,
    Archive,
    Shield,
    Building2,
    MapPin,
    Receipt as ReceiptIcon,
    FileText,
    Hash,
    X,
    Camera,
    Car,
    Train,
    Coffee,
    ShoppingBag,
    ShoppingCart,
    Cloud,
    Apple,
} from "lucide-react";
import PublicHeader from "@/components/PublicHeader";
import ThreeWaysDiagram from "@/components/ThreeWaysDiagram";


// ---------------- Hero — Apple-style dashboard preview ------------------
/**
 * Squircle brand tile inspired by the iOS / macOS App Store icons.
 * - Soft brand gradient
 * - Inner highlight (top) + inner shadow (bottom) for that glassy, dimensional look
 * - White lucide glyph chosen to match the merchant category
 */
function BrandTile({ icon: Icon, gradient, size = 40 }) {
    return (
        <span
            className="relative inline-flex items-center justify-center shrink-0 overflow-hidden"
            style={{
                width: size,
                height: size,
                borderRadius: size * 0.28,
                background: gradient,
                boxShadow:
                    "0 1px 1px rgba(0,0,0,0.08), 0 6px 14px -6px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.35), inset 0 -1px 0 rgba(0,0,0,0.15)",
            }}
        >
            {/* Subtle top gloss */}
            <span
                aria-hidden
                className="absolute inset-x-0 top-0 h-1/2 opacity-60"
                style={{
                    background:
                        "linear-gradient(to bottom, rgba(255,255,255,0.35), rgba(255,255,255,0))",
                }}
            />
            <Icon
                className="relative text-white drop-shadow-sm"
                style={{ width: size * 0.5, height: size * 0.5 }}
                strokeWidth={2.25}
            />
        </span>
    );
}

function DashboardPreview() {
    // Curated brand pool — gradients tuned to feel like SF/App Store icons.
    const POOL = useMemo(
        () => [
            {
                merchant: "Uber",
                amount: "23,40 €",
                icon: Car,
                gradient: "linear-gradient(135deg, #1A1A1A 0%, #3A3A3C 100%)",
                score: 95,
            },
            {
                merchant: "Amazon Business",
                amount: "47,90 €",
                icon: ShoppingBag,
                gradient: "linear-gradient(135deg, #FF9900 0%, #FFB347 100%)",
                score: 97,
            },
            {
                merchant: "SNCF Connect",
                amount: "112,00 €",
                icon: Train,
                gradient: "linear-gradient(135deg, #0064B2 0%, #2E8BD6 100%)",
                score: 94,
            },
            {
                merchant: "Le Pain Quotidien",
                amount: "12,50 €",
                icon: Coffee,
                gradient: "linear-gradient(135deg, #8B5A2B 0%, #B07B47 100%)",
                score: 99,
            },
            {
                merchant: "OVHcloud",
                amount: "39,99 €",
                icon: Cloud,
                gradient: "linear-gradient(135deg, #123F6D 0%, #2A6FB5 100%)",
                score: 100,
            },
            {
                merchant: "Notion",
                amount: "8,00 €",
                icon: FileText,
                gradient: "linear-gradient(135deg, #1D1D1F 0%, #48484A 100%)",
                score: 96,
            },
            {
                merchant: "Carrefour Pro",
                amount: "65,20 €",
                icon: ShoppingCart,
                gradient: "linear-gradient(135deg, #004C8C 0%, #0A6FCB 100%)",
                score: 92,
            },
            {
                merchant: "Apple Store",
                amount: "1 199,00 €",
                icon: Apple,
                gradient: "linear-gradient(135deg, #1D1D1F 0%, #6E6E73 100%)",
                score: 100,
            },
        ],
        [],
    );
    const [offset, setOffset] = useState(0);
    const [hoveredId, setHoveredId] = useState(null);
    // Manual-row scan demo state: "idle" → "scanning" → "matched" → back to "idle"
    const [scanState, setScanState] = useState("idle");

    const runScan = () => {
        if (scanState !== "idle") return;
        setScanState("scanning");
        // ~1.4s laser sweep, then reveal match
        window.setTimeout(() => setScanState("matched"), 1500);
        // Hold the success state for ~2s, then reset
        window.setTimeout(() => setScanState("idle"), 4200);
    };

    // Auto-rotate every 4s — pause on hover
    useEffect(() => {
        if (hoveredId !== null) return;
        const t = setInterval(() => setOffset((o) => (o + 1) % POOL.length), 4000);
        return () => clearInterval(t);
    }, [hoveredId, POOL.length]);

    const visible = [0, 1, 2].map((i) => POOL[(offset + i) % POOL.length]);

    return (
        <div className="relative tk-float" data-testid="hero-dashboard-preview">
            <div
                className="absolute -inset-12 bg-gradient-to-tr from-[#007AFF]/15 via-transparent to-[#34C759]/15 blur-3xl rounded-full"
                aria-hidden
            />
            <div className="relative tk-shadow-float rounded-3xl bg-white border border-black/5 overflow-hidden">
                {/* macOS-style window header */}
                <div className="flex items-center gap-2 px-4 py-3 border-b border-black/5 bg-[#FAFAFA]">
                    <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57] tk-pulse-soft" />
                    <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E] tk-pulse-soft tk-pulse-soft-1" />
                    <span className="w-2.5 h-2.5 rounded-full bg-[#28C840] tk-pulse-soft tk-pulse-soft-2" />
                    <span className="ml-3 text-xs font-medium text-[#86868B]">ticketify.fr · dashboard</span>
                    <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] font-semibold text-[#86868B] uppercase tracking-wider">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#34C759] tk-live-dot" />
                        Live
                    </span>
                </div>

                <div className="p-5 space-y-4">
                    {/* Headline + progress */}
                    <div className="flex items-center gap-2">
                        <Zap className="w-4 h-4 text-[#007AFF] tk-pulse-soft" />
                        <span className="text-xs font-bold uppercase tracking-wider text-[#86868B]">
                            Automatisation
                        </span>
                        <span className="text-xs font-bold text-[#1D1D1F] tabular-nums ml-auto">
                            <AnimatedNumber value={84} suffix=" %" />
                        </span>
                    </div>
                    <div className="relative h-2 bg-[#F5F5F7] rounded-full overflow-hidden">
                        <div
                            className="h-full bg-gradient-to-r from-[#007AFF] to-[#34C759] rounded-full tk-progress-animated"
                            style={{ width: "84%" }}
                        />
                        <div className="absolute inset-y-0 left-0 tk-shimmer-bar" style={{ width: "84%" }} />
                    </div>

                    {/* Auto-matched rows — 3 rotating */}
                    <div className="space-y-2">
                        {visible.map((t, i) => (
                            <button
                                key={`${t.merchant}-${offset}-${i}`}
                                type="button"
                                onMouseEnter={() => setHoveredId(i)}
                                onMouseLeave={() => setHoveredId(null)}
                                className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#FAFAFA] hover:bg-white hover:shadow-md transition-all duration-300 cursor-pointer text-left group tk-row-rise"
                                style={{ animationDelay: `${i * 80}ms` }}
                            >
                                <BrandTile icon={t.icon} gradient={t.gradient} size={40} />
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-[#1D1D1F] truncate tracking-tight">
                                        {t.merchant}
                                    </p>
                                    <p className="text-xs text-[#86868B] tabular-nums">
                                        {t.amount}
                                        <span className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-[#34C759]">
                                            • Justifié automatiquement
                                        </span>
                                    </p>
                                </div>
                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full bg-[#34C759]/15 text-[#34C759] tabular-nums">
                                    <Check className="w-3 h-3" />
                                    <AnimatedNumber value={t.score} suffix="%" />
                                </span>
                            </button>
                        ))}
                    </div>

                    {/* --- Divider --- */}
                    <div className="flex items-center gap-2 pt-1">
                        <span className="h-px flex-1 bg-gradient-to-r from-transparent via-black/10 to-transparent" />
                        <span className="text-[9px] font-bold uppercase tracking-[0.16em] text-[#86868B]">
                            {scanState === "matched" ? "Justifié manuellement" : "À justifier manuellement"}
                        </span>
                        <span className="h-px flex-1 bg-gradient-to-r from-transparent via-black/10 to-transparent" />
                    </div>

                    {/* Manual receipt row — paper ticket scan CTA + live demo */}
                    <div
                        className={`tk-manual-row tk-manual-row--${scanState} relative flex items-center gap-3 p-3 rounded-xl tk-row-rise overflow-hidden`}
                        style={{ animationDelay: "260ms" }}
                        data-testid="hero-manual-row"
                        data-state={scanState}
                    >
                        {/* Left edge halo — amber when idle, green when matched */}
                        <span
                            aria-hidden
                            className="tk-manual-edge absolute inset-y-0 left-0 w-1 rounded-l-xl tk-pulse-soft"
                        />

                        {/* Scanning overlay: corner brackets + laser sweep */}
                        {scanState === "scanning" && (
                            <span aria-hidden className="absolute inset-0 pointer-events-none">
                                <span className="tk-scan-bracket tk-scan-bracket--tl" />
                                <span className="tk-scan-bracket tk-scan-bracket--tr" />
                                <span className="tk-scan-bracket tk-scan-bracket--bl" />
                                <span className="tk-scan-bracket tk-scan-bracket--br" />
                                <span className="tk-scan-laser" />
                                <span className="tk-scan-tint" />
                            </span>
                        )}

                        {/* Brand tile — Coffee when idle/scanning, Check when matched */}
                        {scanState === "matched" ? (
                            <BrandTile
                                icon={Check}
                                gradient="linear-gradient(135deg, #1FAF54 0%, #34C759 100%)"
                                size={40}
                            />
                        ) : (
                            <BrandTile
                                icon={Coffee}
                                gradient="linear-gradient(135deg, #6B4226 0%, #8E5A36 100%)"
                                size={40}
                            />
                        )}

                        <div className="flex-1 min-w-0 relative">
                            <p className="tk-manual-title text-sm font-semibold truncate tracking-tight">
                                Café de Flore
                            </p>
                            {scanState === "matched" ? (
                                <p className="text-xs tabular-nums font-medium text-[#1FAF54]">
                                    18,40 €
                                    <span className="tk-manual-sub-muted font-normal"> · Justifié manuellement</span>
                                </p>
                            ) : (
                                <p className="text-xs text-[#FF9500] tabular-nums font-medium">
                                    18,40 €
                                    <span className="tk-manual-sub-muted font-normal">
                                        {scanState === "scanning" ? " · Analyse OCR en cours…" : " · Reçu papier manquant"}
                                    </span>
                                </p>
                            )}
                        </div>

                        {/* Trailing CTA / badge */}
                        {scanState === "matched" ? (
                            <span
                                className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full bg-[#34C759]/15 text-[#34C759] tabular-nums tk-reveal-in"
                                data-testid="hero-scan-badge"
                            >
                                <Check className="w-3 h-3" />
                                100 %
                            </span>
                        ) : (
                            <button
                                type="button"
                                onClick={runScan}
                                disabled={scanState !== "idle"}
                                data-testid="hero-scan-cta"
                                aria-label="Démarrer la démo de scan OCR"
                                className="relative inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-full text-white shadow-sm transition-all duration-300 hover:shadow-md hover:scale-[1.04] active:scale-[0.98] disabled:cursor-wait"
                                style={{
                                    background:
                                        "linear-gradient(135deg, #007AFF 0%, #0A84FF 100%)",
                                    boxShadow:
                                        "0 1px 0 rgba(255,255,255,0.25) inset, 0 4px 10px -4px rgba(10,132,255,0.55)",
                                }}
                            >
                                <Camera
                                    className={`w-3.5 h-3.5 ${scanState === "scanning" ? "tk-camera-spin" : ""}`}
                                />
                                {scanState === "scanning" ? "Scan…" : "Scanner"}
                            </button>
                        )}
                    </div>

                    {/* Subtle hint */}
                    <p className="text-[10px] text-[#86868B] text-center pt-1">
                        Survolez pour explorer · auto-rotation toutes les 4 sec
                    </p>
                </div>
            </div>
        </div>
    );
}

/** Number that counts up from 0 to `value` once on mount. Restarts when value changes. */
function AnimatedNumber({ value, suffix = "" }) {
    const [display, setDisplay] = useState(0);
    useEffect(() => {
        let raf;
        const start = performance.now();
        const dur = 800;
        const from = display;
        const to = value;
        const tick = (now) => {
            const t = Math.min(1, (now - start) / dur);
            const eased = 1 - Math.pow(1 - t, 3);
            setDisplay(Math.round(from + (to - from) * eased));
            if (t < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);
    return <>{display}{suffix}</>;
}


// -------- Product Proof — Stripe × Linear style detail fiche -----
function ProductProofMockup() {
    return (
        <div className="relative tk-rise tk-rise-2">
            {/* Ambient blue/green glow — same gradient family as hero */}
            <div
                className="absolute -inset-16 bg-gradient-to-br from-[#007AFF]/15 via-transparent to-[#34C759]/15 blur-3xl rounded-full"
                aria-hidden
            />
            <div className="relative tk-shadow-float rounded-3xl bg-white border border-black/5 overflow-hidden">
                {/* Window chrome */}
                <div className="flex items-center gap-2 px-5 py-3.5 border-b border-black/5 bg-[#FAFAFA]">
                    <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                    <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                    <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
                    <span className="ml-3 text-xs font-medium text-[#86868B]">
                        ticketify.fr / mes-tickets / REC-2026-0048
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-[#34C759]">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#34C759] tk-live-dot" />
                        Ticket retrouvé en 8 sec
                    </span>
                </div>

                <div className="grid md:grid-cols-[1.35fr_1fr]">
                    {/* LEFT — receipt detail */}
                    <div className="p-7 sm:p-9 md:border-r border-black/5">
                        {/* Header */}
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#86868B]">
                                    Restaurant · 14 fév. 2026 · 13:42
                                </p>
                                <h3 className="mt-1.5 text-2xl sm:text-[28px] font-semibold tracking-tight text-[#1D1D1F]">
                                    Le Bristol Paris
                                </h3>
                                <p className="text-sm text-[#86868B] mt-1">8 rue du Faubourg Saint-Honoré · 75008 Paris</p>
                            </div>
                            <span className="shrink-0 inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full bg-[#34C759]/12 text-[#34C759] tk-pulse-soft">
                                <Check className="w-3 h-3" /> Trouvé tout seul
                            </span>
                        </div>

                        {/* Line items */}
                        <div className="mt-8 space-y-2.5">
                            {[
                                { name: "Menu Affaires (entrée + plat)", qty: 2, price: 78.0 },
                                { name: "Eau plate Évian 75cl", qty: 1, price: 8.5 },
                                { name: "Café", qty: 2, price: 9.0 },
                                { name: "Service compris", qty: null, price: null, muted: true },
                            ].map((l, i) => (
                                <div
                                    key={l.name}
                                    className="flex items-center justify-between text-sm tk-rise"
                                    style={{ animationDelay: `${260 + i * 80}ms` }}
                                >
                                    <div className="flex items-baseline gap-3 min-w-0">
                                        {l.qty != null && (
                                            <span className="text-[11px] font-mono text-[#AEAEB2] tabular-nums w-5 shrink-0">
                                                ×{l.qty}
                                            </span>
                                        )}
                                        <span className={`truncate ${l.muted ? "text-[#AEAEB2] italic" : "text-[#1D1D1F]"}`}>
                                            {l.name}
                                        </span>
                                    </div>
                                    <span
                                        className={`tabular-nums font-medium ${l.muted ? "text-[#AEAEB2]" : "text-[#1D1D1F]"}`}
                                    >
                                        {l.price != null ? `${l.price.toFixed(2).replace(".", ",")} €` : "—"}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {/* Totals */}
                        <div className="mt-7 pt-5 border-t border-dashed border-black/10 space-y-2">
                            <Row label="Sous-total HT" value="80,73 €" />
                            <Row label="TVA 10 %" value="14,07 €" accent="green" testId="proof-vat" />
                            <div className="flex items-center justify-between pt-3">
                                <span className="text-base font-semibold text-[#1D1D1F]">Total payé</span>
                                <span className="text-2xl font-semibold tracking-tight text-[#1D1D1F] tabular-nums">
                                    94,80 €
                                </span>
                            </div>
                        </div>

                        {/* Meta */}
                        <div className="mt-6 grid grid-cols-2 gap-3 text-xs">
                            <Meta icon={Hash} label="Facture" value="FAC-2026-1142" />
                            <Meta icon={Building2} label="SIRET" value="552 100 554 00031" />
                        </div>
                    </div>

                    {/* RIGHT — bank match + meta */}
                    <div className="p-7 sm:p-9 bg-[#FAFAFA]">
                        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#86868B]">
                            Transaction liée
                        </p>

                        {/* Bank tx card */}
                        <div className="mt-3 rounded-2xl bg-white border border-black/5 p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#0A2540] to-[#1D1D1F] flex items-center justify-center text-white shadow-sm">
                                    <CreditCard className="w-4 h-4" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-[#1D1D1F]">Carte Visa Business</p>
                                    <p className="text-xs text-[#86868B]">•• 4242 · 14 fév. 13:43</p>
                                </div>
                                <p className="text-sm font-semibold tabular-nums text-[#1D1D1F]">94,80 €</p>
                            </div>
                            <div className="mt-3 flex items-center justify-between text-[11px]">
                                <span className="inline-flex items-center gap-1 text-[#34C759] font-semibold">
                                    <span className="w-1.5 h-1.5 rounded-full bg-[#34C759] tk-live-dot" />
                                    Collé au bon ticket
                                </span>
                                <span className="text-[#86868B]">Auto · venu du Gmail</span>
                            </div>
                        </div>

                        {/* Match flow */}
                        <div className="mt-5 relative rounded-2xl bg-white border border-black/5 p-4 overflow-hidden">
                            <div className="absolute inset-x-0 top-1/2 h-px bg-gradient-to-r from-[#007AFF]/0 via-[#007AFF]/50 to-[#34C759]/0 tk-match-line" aria-hidden />
                            <div className="relative flex items-center justify-between text-[11px]">
                                <Pill icon={CreditCard} label="Vous payez" />
                                <Pill icon={Mail} label="On lit le mail" />
                                <Pill icon={Archive} label="C'est rangé" highlight />
                            </div>
                        </div>

                        {/* Documents */}
                        <p className="mt-6 text-[11px] font-bold uppercase tracking-[0.18em] text-[#86868B]">
                            Vos pièces justificatives
                        </p>
                        <div className="mt-3 space-y-2">
                            <Doc title="facture-FAC-2026-1142.pdf" sub="Bonne pour la compta · 124 ko" />
                            <Doc title="email-original.eml" sub="Reçu d'origine · conservé" muted />
                        </div>

                        {/* Hosting note */}
                        <div className="mt-6 flex items-center gap-2 text-[11px] text-[#86868B]">
                            <Shield className="w-3.5 h-3.5 text-[#34C759]" />
                            Gardé en sécurité en France · 10 ans
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Row({ label, value, accent, testId }) {
    const valueColor =
        accent === "green" ? "text-[#34C759]" : "text-[#1D1D1F]";
    return (
        <div className="flex items-center justify-between text-sm" data-testid={testId}>
            <span className="text-[#86868B]">{label}</span>
            <span className={`tabular-nums font-medium ${valueColor}`}>{value}</span>
        </div>
    );
}

function Meta({ icon: Icon, label, value }) {
    return (
        <div className="flex items-center gap-2 rounded-lg bg-[#F5F5F7] px-3 py-2">
            <Icon className="w-3.5 h-3.5 text-[#86868B]" />
            <div className="min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#86868B]">{label}</p>
                <p className="text-xs font-medium text-[#1D1D1F] truncate">{value}</p>
            </div>
        </div>
    );
}

function Pill({ icon: Icon, label, highlight }) {
    return (
        <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-semibold tracking-wider uppercase text-[10px] ${
                highlight
                    ? "bg-gradient-to-r from-[#007AFF] to-[#34C759] text-white"
                    : "bg-[#F5F5F7] text-[#1D1D1F] border border-black/5"
            }`}
        >
            <Icon className="w-3 h-3" />
            {label}
        </span>
    );
}

function Doc({ title, sub, muted }) {
    return (
        <div className={`flex items-center gap-3 rounded-xl border border-black/5 px-3 py-2.5 ${muted ? "bg-[#F5F5F7]" : "bg-white"}`}>
            <FileText className="w-4 h-4 text-[#86868B] shrink-0" />
            <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-[#1D1D1F] truncate">{title}</p>
                <p className="text-[10px] text-[#86868B] truncate">{sub}</p>
            </div>
            {!muted && <Check className="w-3.5 h-3.5 text-[#34C759]" />}
        </div>
    );
}


// --------- TPE 3-case card ---------------------------------------
function TpeCase({ i, badge, badgeColor, example, title, body, footer, chip }) {
    const isAuto = chip === "auto";
    return (
        <div
            className="tk-card p-7 tk-rise flex flex-col"
            style={{ animationDelay: `${i * 120}ms` }}
            data-testid={`tpe-case-${i}`}
        >
            <div className="flex items-center justify-between mb-5">
                <span
                    className="text-[10px] font-bold uppercase tracking-[0.15em] px-2.5 py-1 rounded-full"
                    style={{ color: badgeColor, backgroundColor: `${badgeColor}18` }}
                >
                    {badge}
                </span>
                <span
                    className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider"
                    style={{ color: isAuto ? "#34C759" : "#FF9F0A" }}
                >
                    {isAuto ? <Check className="w-3 h-3" /> : <Camera className="w-3 h-3" />}
                    {isAuto ? "Auto" : "Photo"}
                </span>
            </div>
            <p className="text-xs text-[#86868B] mb-2 italic">{example}</p>
            <h3 className="text-lg font-semibold text-[#1D1D1F]">{title}</h3>
            <p className="mt-2 text-sm text-[#86868B] leading-relaxed flex-1">{body}</p>
            <p
                className="mt-5 pt-4 border-t border-black/5 text-xs font-medium"
                style={{ color: badgeColor }}
            >
                {footer}
            </p>
        </div>
    );
}


// ---------------- Steps (3-step "Comment ça marche") --------------
const FLOW_STEPS = [
    {
        n: "01",
        icon: CreditCard,
        title: "Vous payez par carte",
        body: "Comme d'habitude. Au resto, en ligne, en déplacement.",
        chip: "0 sec",
    },
    {
        n: "02",
        icon: Sparkles,
        title: "On retrouve le ticket",
        body: "On va le chercher dans votre Gmail et on le colle à la bonne dépense.",
        chip: "< 30 sec",
    },
    {
        n: "03",
        icon: Archive,
        title: "Tout est rangé pour vous",
        body: "Montant, TVA, date, marchand. Prêt à donner au comptable.",
        chip: "Auto",
    },
];

// ---------------- Trust strip ------------------------------------
const TRUSTS = [
    { icon: MapPin, label: "Données en France" },
    { icon: Shield, label: "Confidentialité totale" },
    { icon: Building2, label: "Toutes les banques pros" },
    { icon: ReceiptIcon, label: "TVA repérée toute seule" },
    { icon: Archive, label: "Gardé en sécurité 10 ans" },
];

const TRUSTED_MERCHANTS = [
    "Amazon", "Uber", "SNCF", "Apple", "OVH", "Notion",
    "Booking", "Airbnb", "Air France", "Stripe", "Microsoft", "Carrefour Drive",
];


export default function Landing() {
    const { user } = useAuth();
    const ctaTarget = user ? "/app" : "/register";
    const ctaLabel = user ? "Aller au tableau de bord" : "Commencer gratuitement";
    return (
        <div className="min-h-screen relative overflow-hidden">
            <PublicHeader />

            {/* HERO */}
            <section className="relative pt-32 pb-20 sm:pt-44 sm:pb-28 px-6 sm:px-8">
                <div className="relative max-w-6xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7 tk-rise">
                        <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#007AFF] mb-6 bg-white border border-[#007AFF]/20 px-3 py-1.5 rounded-full tk-shadow-card">
                            <Zap className="w-3 h-3" /> Justificatif automatique à chaque paiement
                        </p>
                        <h1 className="text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight text-[#1D1D1F] leading-[1.02]">
                            La fin du<br />
                            <span className="bg-gradient-to-r from-[#007AFF] to-[#34C759] bg-clip-text text-transparent">
                                chaos comptable.
                            </span>
                        </h1>
                        <p className="mt-7 text-xl sm:text-2xl text-[#1D1D1F]/85 leading-snug max-w-xl tk-hero-body font-medium">
                            Préparez la dématérialisation des factures.
                        </p>
                        <p className="mt-4 text-base sm:text-lg text-[#86868B] leading-relaxed max-w-xl">
                            Connectez votre carte pro et votre Gmail. À chaque paiement, on retrouve le ticket à votre place.
                        </p>
                        <div className="mt-10 flex flex-wrap items-center gap-4">
                            <Link
                                to={ctaTarget}
                                className="tk-btn tk-btn-primary text-base px-7 py-3.5 tk-cta-glow"
                                data-testid="hero-cta-register"
                            >
                                {ctaLabel} <ArrowRight className="w-4 h-4" />
                            </Link>
                            <Link
                                to="/comment-ca-marche"
                                className="text-sm font-semibold text-[#1D1D1F] hover:text-[#007AFF] transition inline-flex items-center gap-1.5"
                                data-testid="hero-cta-learn"
                            >
                                Voir comment ça marche →
                            </Link>
                        </div>
                        <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[#86868B]">
                            <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-[#34C759]" /> 100 transactions justifiées gratuitement</span>
                            <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-[#34C759]" /> Aucune carte requise</span>
                            <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-[#34C759]" /> RGPD · données en France</span>
                        </div>
                    </div>
                    <div className="lg:col-span-5 tk-rise tk-rise-2">
                        <DashboardPreview />
                    </div>
                </div>
            </section>

            {/* THREE-WAYS DIAGRAM — first thing after hero */}
            <section
                className="relative px-6 sm:px-8 pb-20 sm:pb-24"
                data-testid="three-ways-section"
            >
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-10 sm:mb-12">
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#86868B] mb-3 tk-rise">
                            Comment on capture vos tickets
                        </p>
                        <h2 className="text-3xl sm:text-5xl font-semibold tracking-tight text-[#1D1D1F] tk-rise tk-rise-1 leading-[1.05]">
                            Trois façons.{" "}
                            <span className="bg-gradient-to-r from-[#007AFF] to-[#34C759] bg-clip-text text-transparent">
                                Zéro effort.
                            </span>
                        </h2>
                        <p className="mt-4 max-w-2xl mx-auto text-base sm:text-lg text-[#86868B] tk-rise tk-rise-2">
                            Vos reçus Gmail, votre relevé de banque, ou une photo. On capture tout. Vous, vous payez — c'est tout.
                        </p>
                    </div>
                    <ThreeWaysDiagram />
                </div>
            </section>

            {/* PRODUCT PROOF — receipt fiche détaillée */}
            <section
                className="relative px-6 sm:px-8 pb-24 sm:pb-32"
                data-testid="product-proof"
            >
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-12 sm:mb-16">
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#86868B] mb-3 tk-rise">
                            Aperçu produit
                        </p>
                        <h2 className="text-3xl sm:text-5xl font-semibold tracking-tight text-[#1D1D1F] tk-rise tk-rise-1 leading-[1.05]">
                            Un ticket de 94,80 € au Bristol.{" "}
                            <span className="bg-gradient-to-r from-[#007AFF] to-[#34C759] bg-clip-text text-transparent">
                                Trouvé en 8 secondes.
                            </span>
                        </h2>
                        <p className="mt-4 text-base sm:text-lg text-[#86868B] max-w-2xl mx-auto tk-rise tk-rise-2">
                            Pas une maquette. C'est la fiche que vous voyez juste après avoir réglé l'addition — montant, articles, TVA, SIRET, facture PDF. Tout est là. Tout seul.
                        </p>
                    </div>
                    <ProductProofMockup />
                </div>
            </section>

            {/* TRUST STRIP */}
            <section
                className="border-y border-black/5 bg-white/50 backdrop-blur-sm py-7 px-6 sm:px-8"
                data-testid="trust-strip"
            >
                <div className="max-w-6xl mx-auto">
                    <ul className="flex flex-wrap items-center justify-center gap-x-9 gap-y-3 text-sm">
                        {TRUSTS.map((t) => (
                            <li
                                key={t.label}
                                className="inline-flex items-center gap-2 font-medium tk-trust-item"
                            >
                                <t.icon className="w-4 h-4 text-[#34C759]" />
                                {t.label}
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* AT THE TPE — answer to objection #1 */}
            <section className="px-6 sm:px-8 py-24 sm:py-28" data-testid="tpe-section">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-14">
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#86868B] mb-3">
                            Et au TPE physique ?
                        </p>
                        <h2 className="text-3xl sm:text-5xl font-semibold tracking-tight text-[#1D1D1F] leading-[1.05]">
                            Une question.{" "}
                            <span className="bg-gradient-to-r from-[#007AFF] to-[#34C759] bg-clip-text text-transparent">
                                Trois réponses.
                            </span>
                        </h2>
                        <p className="mt-4 text-base sm:text-lg text-[#86868B] max-w-2xl mx-auto">
                            Le terminal de paiement ne donne pas le détail du ticket. Mais on a une solution pour chaque cas.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-5">
                        <TpeCase
                            i={0}
                            badge="Carte fidélité"
                            badgeColor="#34C759"
                            example="Carrefour, Monoprix, Decathlon…"
                            title="E-ticket envoyé par mail"
                            body="Présentez votre carte fidélité au TPE. L'enseigne envoie le détail par mail."
                            footer="100 % auto · activez l'opt-in une fois"
                            chip="auto"
                        />
                        <TpeCase
                            i={1}
                            badge="Caisse connectée"
                            badgeColor="#007AFF"
                            example="Sunday, Yavin, Square, SumUp"
                            title="Vous donnez juste votre mail"
                            body="QR code à table ou bouton à la caisse. Le ticket part sur votre boîte dans la minute."
                            footer="100 % auto · de plus en plus de restos"
                            chip="auto"
                        />
                        <TpeCase
                            i={2}
                            badge="Petit commerce"
                            badgeColor="#FF9F0A"
                            example="Boulanger, bistrot, pharmacie…"
                            title="Photo en 7 secondes"
                            body="L'onglet Transactions affiche la ligne. Vous tap « Ajouter photo », l'IA fait le reste."
                            footer="7 sec · puis vous jetez le papier"
                            chip="photo"
                        />
                    </div>

                    <div className="mt-10 text-center">
                        <Link
                            to="/comment-ca-marche"
                            className="text-sm font-semibold text-[#007AFF] hover:underline"
                            data-testid="tpe-more-link"
                        >
                            Voir tous les cas en détail →
                        </Link>
                    </div>
                </div>
            </section>

            {/* COMMENT ÇA MARCHE — 3 steps visual flow */}
            <section
                id="how"
                className="py-24 sm:py-32 px-6 sm:px-8 relative"
                data-testid="how-section"
            >
                <div className="relative max-w-6xl mx-auto">
                    <div className="text-center max-w-2xl mx-auto mb-16">
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#86868B] mb-4">
                            Comment ça marche
                        </p>
                        <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight text-[#1D1D1F] leading-[1.05]">
                            Trois étapes.{" "}
                            <span className="bg-gradient-to-r from-[#007AFF] to-[#34C759] bg-clip-text text-transparent">
                                Aucune action.
                            </span>
                        </h2>
                    </div>

                    {/* Connecting line (desktop only) */}
                    <div className="relative grid md:grid-cols-3 gap-6">
                        <div className="hidden md:block absolute inset-x-0 top-[88px] h-px bg-gradient-to-r from-transparent via-[#007AFF]/30 to-transparent" aria-hidden />
                        {FLOW_STEPS.map((s, i) => (
                            <div
                                key={s.n}
                                className="relative tk-card p-7 tk-rise transition-all hover:-translate-y-1 hover:tk-shadow-elev"
                                style={{ animationDelay: `${i * 120}ms` }}
                                data-testid={`flow-step-${i}`}
                            >
                                <div className="flex items-center justify-between mb-6">
                                    <span className="text-sm font-bold text-[#AEAEB2] tabular-nums">{s.n}</span>
                                    <span className="text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full bg-[#007AFF]/10 text-[#007AFF]">
                                        {s.chip}
                                    </span>
                                </div>
                                <div className="w-12 h-12 rounded-2xl bg-[#F5F5F7] border border-black/5 shadow-sm flex items-center justify-center mb-5">
                                    <s.icon className="w-6 h-6 text-[#1D1D1F]" />
                                </div>
                                <h3 className="text-lg font-semibold text-[#1D1D1F] mb-2">{s.title}</h3>
                                <p className="text-sm text-[#86868B] leading-relaxed">{s.body}</p>
                            </div>
                        ))}
                    </div>
                    <div className="mt-10 text-center">
                        <Link
                            to="/comment-ca-marche"
                            className="text-sm font-semibold text-[#007AFF] hover:underline"
                            data-testid="how-more-link"
                        >
                            Voir le détail technique →
                        </Link>
                    </div>
                </div>
            </section>

            {/* BEFORE / AFTER — visual transformation, SaaS-style */}
            <section className="py-24 sm:py-28 px-6 sm:px-8 relative" data-testid="before-after">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-14 max-w-2xl mx-auto">
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#86868B] mb-3">
                            Avant / Après
                        </p>
                        <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight text-[#1D1D1F] leading-[1.05]">
                            Le jour&nbsp;et la nuit.
                        </h2>
                        <p className="mt-5 text-lg text-[#86868B] leading-relaxed">
                            Avant Ticketify, un dirigeant pro perd en moyenne <strong className="text-[#1D1D1F]">3 h par semaine</strong> à traquer ses tickets et <strong className="text-[#1D1D1F]">17 % de TVA récupérable</strong> chaque année. Reprenez ce temps. Et cet argent.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6 relative">
                        {/* BEFORE — visually messy mockup */}
                        <div className="tk-card p-7 sm:p-8 tk-rise overflow-hidden bg-[#FFF8F7]" data-testid="before-card">
                            <div className="flex items-center justify-between mb-6">
                                <div className="inline-flex items-center gap-2.5">
                                    <div className="w-8 h-8 rounded-xl bg-[#FF3B30]/12 flex items-center justify-center">
                                        <X className="w-4 h-4 text-[#FF3B30]" strokeWidth={2.5} />
                                    </div>
                                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#FF3B30]">
                                        Sans Ticketify
                                    </p>
                                </div>
                                <span className="text-[10px] font-bold uppercase tracking-wider text-[#FF3B30]/70 px-2 py-0.5 rounded-full bg-[#FF3B30]/10">
                                    Mardi 23h
                                </span>
                            </div>

                            {/* "Messy desk" mockup — overlapping faded receipts with strikethroughs */}
                            <div className="relative h-44 mb-6 -mx-2">
                                {[
                                    { txt: "Uber · 23,40 €", rot: "-6deg", x: "4%", y: "8%", opacity: 0.55 },
                                    { txt: "Carrefour · 87,90 €", rot: "4deg", x: "32%", y: "0%", opacity: 0.7 },
                                    { txt: "Apple · ??", rot: "-3deg", x: "58%", y: "12%", opacity: 0.4 },
                                    { txt: "SNCF · perdu", rot: "8deg", x: "12%", y: "48%", opacity: 0.45 },
                                    { txt: "WeWork · 240 €", rot: "-9deg", x: "42%", y: "52%", opacity: 0.6 },
                                ].map((p) => (
                                    <div
                                        key={p.txt}
                                        className="absolute px-3 py-2 rounded-md bg-white border border-black/10 text-[11px] font-medium tabular-nums text-[#1D1D1F] shadow-sm"
                                        style={{ left: p.x, top: p.y, transform: `rotate(${p.rot})`, opacity: p.opacity }}
                                    >
                                        <span className="line-through decoration-[#FF3B30]/70 decoration-[1.5px]">{p.txt}</span>
                                    </div>
                                ))}
                                {/* Question mark stamps to suggest confusion */}
                                <span className="absolute right-3 top-3 text-3xl font-bold text-[#FF3B30]/30 select-none">?</span>
                                <span className="absolute left-1/2 bottom-2 text-2xl font-bold text-[#FF9F0A]/30 select-none">?</span>
                            </div>

                            <ul className="space-y-3">
                                {[
                                    { stat: "12", unit: "tickets perdus", note: "par mois en moyenne" },
                                    { stat: "3 h", unit: "par semaine", note: "à reconstituer la compta" },
                                    { stat: "—17 %", unit: "TVA non récupérée", note: "argent laissé sur la table" },
                                ].map((row) => (
                                    <li key={row.unit} className="flex items-baseline gap-3 text-[14px]">
                                        <span className="font-semibold text-[#FF3B30] tabular-nums min-w-[48px]">{row.stat}</span>
                                        <span className="text-[#1D1D1F]">{row.unit}</span>
                                        <span className="text-[#86868B] text-xs">— {row.note}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Arrow connector — visible md+ */}
                        <div className="hidden md:flex absolute left-1/2 top-[18rem] -translate-x-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full items-center justify-center bg-white border border-black/10 shadow-lg" aria-hidden>
                            <ArrowRight className="w-5 h-5 text-[#1D1D1F]" strokeWidth={2.5} />
                        </div>

                        {/* AFTER — clean mini-dashboard preview */}
                        <div className="relative tk-card p-7 sm:p-8 tk-rise tk-rise-1 overflow-hidden" data-testid="after-card">
                            <div
                                className="absolute -top-24 -right-24 w-80 h-80 bg-gradient-to-br from-[#007AFF]/20 to-[#34C759]/20 blur-3xl rounded-full"
                                aria-hidden
                            />
                            <div className="relative">
                                <div className="flex items-center justify-between mb-6">
                                    <div className="inline-flex items-center gap-2.5">
                                        <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#007AFF] to-[#34C759] flex items-center justify-center">
                                            <Sparkles className="w-4 h-4 text-white" strokeWidth={2.5} />
                                        </div>
                                        <p className="text-xs font-bold uppercase tracking-[0.18em] bg-gradient-to-r from-[#007AFF] to-[#34C759] bg-clip-text text-transparent">
                                            Avec Ticketify
                                        </p>
                                    </div>
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-[#34C759] px-2 py-0.5 rounded-full bg-[#34C759]/10 inline-flex items-center gap-1">
                                        <span className="w-1.5 h-1.5 rounded-full bg-[#34C759]" /> Live
                                    </span>
                                </div>

                                {/* Mini dashboard mockup — aligned rows with green checks */}
                                <div className="bg-white rounded-2xl border border-black/[0.06] divide-y divide-black/[0.04] mb-6 -mx-1">
                                    {[
                                        { brand: "Uber", icon: Car, amount: "23,40 €", grad: "linear-gradient(135deg,#1A1A1A,#3A3A3C)" },
                                        { brand: "Carrefour", icon: ShoppingCart, amount: "87,90 €", grad: "linear-gradient(135deg,#2B5DD9,#1A4FCB)" },
                                        { brand: "Apple", icon: Apple, amount: "1 199 €", grad: "linear-gradient(135deg,#1D1D1F,#3A3A3C)" },
                                        { brand: "SNCF", icon: Train, amount: "62,00 €", grad: "linear-gradient(135deg,#7C2DEB,#5B1AB8)" },
                                        { brand: "WeWork", icon: Building2, amount: "240,00 €", grad: "linear-gradient(135deg,#FF6A00,#E04A00)" },
                                    ].map((row) => (
                                        <div key={row.brand} className="flex items-center gap-3 px-3.5 py-2.5">
                                            <BrandTile icon={row.icon} gradient={row.grad} size={28} />
                                            <span className="flex-1 text-[13px] font-medium text-[#1D1D1F]">{row.brand}</span>
                                            <span className="text-[13px] tabular-nums text-[#1D1D1F] font-semibold">{row.amount}</span>
                                            <Check className="w-4 h-4 text-[#34C759]" strokeWidth={2.5} />
                                        </div>
                                    ))}
                                </div>

                                <ul className="space-y-3">
                                    {[
                                        { stat: "100%", unit: "tickets retrouvés", note: "tout seul" },
                                        { stat: "0 min", unit: "à perdre dessus", note: "on fait tout pour vous" },
                                        { stat: "+17 %", unit: "de TVA récupérée", note: "détectée sur chaque ticket" },
                                    ].map((row) => (
                                        <li key={row.unit} className="flex items-baseline gap-3 text-[14px]">
                                            <span className="font-semibold tabular-nums min-w-[48px] bg-gradient-to-r from-[#007AFF] to-[#34C759] bg-clip-text text-transparent">{row.stat}</span>
                                            <span className="text-[#1D1D1F]">{row.unit}</span>
                                            <span className="text-[#86868B] text-xs">— {row.note}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* ROI stat band below the comparison */}
                    <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-px bg-black/5 rounded-2xl overflow-hidden border border-black/5" data-testid="roi-band">
                        {[
                            { v: "3 h", k: "récupérées chaque semaine" },
                            { v: "+17 %", k: "de TVA récupérable détectée" },
                            { v: "10 ans", k: "conservés pour vous, sans rien faire" },
                        ].map((s) => (
                            <div key={s.k} className="bg-white px-6 py-7 text-center">
                                <p className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#1D1D1F] tabular-nums">{s.v}</p>
                                <p className="mt-1.5 text-sm text-[#86868B]">{s.k}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* MARCHANDS RECONNUS */}
            <section className="border-y border-black/5 bg-white/50 backdrop-blur-sm py-10 px-6 sm:px-8">
                <div className="max-w-6xl mx-auto">
                    <p className="text-center text-xs font-bold uppercase tracking-[0.2em] text-[#86868B] mb-6">
                        Marchands dont les e-reçus sont reconnus automatiquement
                    </p>
                    <div className="flex flex-wrap justify-center gap-x-10 gap-y-4">
                        {TRUSTED_MERCHANTS.map((m) => (
                            <span
                                key={m}
                                className="text-[#1D1D1F] font-semibold text-base sm:text-lg opacity-50 hover:opacity-100 transition tracking-tight"
                            >
                                {m}
                            </span>
                        ))}
                    </div>
                </div>
            </section>

            {/* Final CTA — sharper SaaS commercial */}
            <section className="px-6 sm:px-8 py-24 sm:py-32">
                <div className="relative max-w-4xl mx-auto bg-gradient-to-br from-[#1D1D1F] to-[#2C2C2E] rounded-3xl p-12 sm:p-16 text-center overflow-hidden tk-cta-dark">
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-[#007AFF]/20 rounded-full blur-3xl" aria-hidden />
                    <div className="relative">
                        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-[11px] font-semibold uppercase tracking-wider text-white/85 mb-6">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#34C759]" /> Gratuit jusqu'à 100 transactions justifiées
                        </span>
                        <h2 className="text-3xl sm:text-5xl font-semibold tracking-tight text-white leading-[1.05]">
                            Prêt à ne plus jamais chercher un ticket&nbsp;?
                        </h2>
                        <p className="mt-5 text-lg text-white/70 max-w-xl mx-auto">
                            Vous payez. On retrouve. <strong className="text-white">5 minutes pour démarrer</strong>, rien à faire après.
                        </p>
                        <div className="mt-8 flex flex-wrap justify-center items-center gap-4">
                            <Link
                                to={ctaTarget}
                                className="tk-btn tk-btn-blue text-base px-7 py-3.5 tk-cta-glow"
                                data-testid="cta-bottom-register"
                            >
                                {user ? "Aller au tableau de bord" : "Démarrer gratuitement"} <ArrowRight className="w-4 h-4" />
                            </Link>
                            <Link
                                to="/tarifs"
                                className="text-sm font-semibold text-white/75 hover:text-white transition inline-flex items-center gap-1.5"
                                data-testid="cta-bottom-tarifs"
                            >
                                Voir les tarifs →
                            </Link>
                        </div>
                        <div className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs text-white/55">
                            <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[#34C759]" /> Aucune carte requise</span>
                            <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[#34C759]" /> Désinscription en 1 clic</span>
                            <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[#34C759]" /> RGPD · données en France</span>
                        </div>
                    </div>
                </div>
            </section>

            <footer className="border-t border-black/5 py-12 px-6 sm:px-8 text-sm text-[#86868B]">
                <div className="max-w-6xl mx-auto">
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
                        <div>
                            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#1D1D1F] mb-3">Produit</p>
                            <ul className="space-y-2">
                                <li><Link to="/comment-ca-marche" className="hover:text-[#1D1D1F]">Comment ça marche</Link></li>
                                <li><Link to="/tarifs" className="hover:text-[#1D1D1F]">Tarifs</Link></li>
                                <li><Link to="/honnetete" className="hover:text-[#1D1D1F]">Honnêteté</Link></li>
                                <li><Link to="/faq" className="hover:text-[#1D1D1F]">FAQ</Link></li>
                            </ul>
                        </div>
                        <div>
                            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#1D1D1F] mb-3">Société</p>
                            <ul className="space-y-2">
                                <li><Link to="/a-propos" className="hover:text-[#1D1D1F]">À propos</Link></li>
                                <li><Link to="/securite" className="hover:text-[#1D1D1F]">Sécurité</Link></li>
                                <li><a href="mailto:contact@ticketify.fr" className="hover:text-[#1D1D1F]">Contact</a></li>
                            </ul>
                        </div>
                        <div>
                            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#1D1D1F] mb-3">Légal</p>
                            <ul className="space-y-2">
                                <li><Link to="/cgu" className="hover:text-[#1D1D1F]">CGU / CGV</Link></li>
                                <li><Link to="/confidentialite" className="hover:text-[#1D1D1F]">Confidentialité</Link></li>
                                <li><Link to="/mentions-legales" className="hover:text-[#1D1D1F]">Mentions légales</Link></li>
                            </ul>
                        </div>
                        <div>
                            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#1D1D1F] mb-3">Conformité</p>
                            <ul className="space-y-2">
                                <li className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#34C759]" /> Hébergement France</li>
                                <li className="inline-flex items-center gap-1.5"><Shield className="w-3.5 h-3.5 text-[#34C759]" /> RGPD</li>
                                <li className="inline-flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-[#34C759]" /> Conservation 10 ans</li>
                            </ul>
                        </div>
                    </div>
                    <div className="pt-6 border-t border-black/5 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs">
                        <p>© 2026 Ticketify SAS — Tous droits réservés.</p>
                        <p>Fait avec attention en France 🇫🇷</p>
                    </div>
                </div>
            </footer>
        </div>
    );
}
